import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import { useVenue } from "../VenueProvider";
import { useAuth } from "../AuthProvider";
import { esSoloLectura } from "../../utils/roles";
import { Badge } from "../badge";
import { ProveedorEventComments } from "../EventProveedoresSection";
import {
  ChevronRightIcon,
  ChatBubbleBottomCenterTextIcon,
} from "@heroicons/react/20/solid";

// ---------- Eventos tab ----------
const EventosTab = ({ proveedor, venueId, events, navigate, currentTab = "eventos" }) => {
  // La ficha del proveedor no la abre el viewer; el inversionista sí, y
  // lee los comentarios sin escribir (P-FRA-13).
  const { userInfo } = useAuth();
  const soloLectura = esSoloLectura(userInfo);
  const { costos } = useVenue();
  const [expandedId, setExpandedId] = useState(null);

  // Cuántos comentarios dejamos en cada evento, para no tener que abrir uno
  // por uno a ver si hay algo escrito. Una sola suscripción a la colección
  // del proveedor y se agrupa en memoria: los comentarios traen `eventId`,
  // así que una consulta por fila sería N lecturas para el mismo dato.
  const [comentariosPorEvento, setComentariosPorEvento] = useState({});
  useEffect(() => {
    if (!venueId || !proveedor?.id) {
      setComentariosPorEvento({});
      return;
    }
    const col = collection(db, "venues", venueId, "proveedores", proveedor.id, "comentarios");
    const unsub = onSnapshot(
      col,
      (snap) => {
        const porEvento = {};
        snap.docs.forEach((d) => {
          const eventId = d.data()?.eventId;
          if (eventId) porEvento[eventId] = (porEvento[eventId] || 0) + 1;
        });
        setComentariosPorEvento(porEvento);
      },
      (err) => console.error("Comentarios del proveedor error:", err)
    );
    return unsub;
  }, [venueId, proveedor?.id]);

  // Un proveedor se liga a un evento por DOS caminos y hay que mirar los
  // dos: la asignación (`proveedoresAsignados`) y el dinero (un costo con su
  // `proveedorId`). Mirando solo la asignación, esta lista salía vacía o
  // corta mientras la franja de arriba reportaba pagos de varios eventos —
  // la misma ficha contradiciéndose.
  const eventosConCosto = useMemo(() => {
    const ids = new Set();
    (Array.isArray(costos) ? costos : []).forEach((c) => {
      if (c?.proveedorId === proveedor.id && c?.eventId) ids.add(c.eventId);
    });
    return ids;
  }, [costos, proveedor.id]);

  const assignedEvents = useMemo(() => {
    return (events || [])
      .filter((e) => {
        const asignado =
          Array.isArray(e.proveedoresAsignados) &&
          e.proveedoresAsignados.some((a) => a.proveedorId === proveedor.id);
        return asignado || eventosConCosto.has(e.id);
      })
      .sort((a, b) => {
        const aTime = a.startTime?.toMillis ? a.startTime.toMillis() : 0;
        const bTime = b.startTime?.toMillis ? b.startTime.toMillis() : 0;
        return bTime - aTime;
      });
  }, [events, proveedor.id, eventosConCosto]);

  const formatDate = (ts) => {
    if (!ts?.toDate) return "—";
    return ts.toDate().toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (assignedEvents.length === 0) {
    return (
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm p-8 text-center text-sm text-zinc-400 italic">
        Este proveedor no aparece en ningún evento todavía.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {assignedEvents.map((e) => {
        const isExpanded = expandedId === e.id;
        const comentarios = comentariosPorEvento[e.id] || 0;
        return (
          <div
            key={e.id}
            className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm overflow-hidden"
          >
            <div className="flex items-center gap-3 px-4 py-3">
              <button
                type="button"
                onClick={() => setExpandedId(isExpanded ? null : e.id)}
                className="flex items-center gap-2 flex-1 min-w-0 text-left hover:opacity-80"
                title={
                  isExpanded
                    ? "Ocultar comentarios"
                    : comentarios > 0
                    ? `Ver ${comentarios} ${comentarios === 1 ? "comentario" : "comentarios"}`
                    : "Dejar un comentario"
                }
              >
                <ChevronRightIcon
                  className={`h-4 w-4 text-zinc-400 transition-transform ${
                    isExpanded ? "rotate-90" : ""
                  }`}
                />
                <span className="font-medium text-zinc-900 truncate">
                  {e.nombre || "(sin nombre)"}
                </span>
                {e.plannerId === proveedor.id && (
                  <Badge color="indigo">Planner</Badge>
                )}
                {comentarios > 0 && (
                  <span
                    className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-zinc-500"
                    title={`${comentarios} ${comentarios === 1 ? "comentario" : "comentarios"}`}
                  >
                    <ChatBubbleBottomCenterTextIcon className="h-3.5 w-3.5" />
                    {comentarios}
                  </span>
                )}
              </button>
              <span className="text-sm text-zinc-500 whitespace-nowrap">
                {formatDate(e.startTime)}
              </span>
              {e.status === "cancelado" ? (
                <Badge color="red">Cancelado</Badge>
              ) : (
                <Badge color="emerald">Activo</Badge>
              )}
              <button
                type="button"
                onClick={() =>
                  navigate(`/dashboard/eventos/${e.id}`, {
                    state: {
                      from: "proveedor",
                      proveedorId: proveedor.id,
                      proveedorTab: currentTab,
                    },
                  })
                }
                className="text-xs font-medium text-[#1b4d2e] hover:underline whitespace-nowrap"
              >
                Ver evento →
              </button>
            </div>
            {isExpanded && (
              <div className="border-t border-zinc-100 px-4 py-3 bg-zinc-50/30">
                <ProveedorEventComments
                  venueId={venueId}
                  proveedorId={proveedor.id}
                  proveedorNombre={proveedor.nombre}
                  eventId={e.id}
                  eventNombre={e.nombre}
                  isViewer={false}
                  soloLectura={soloLectura}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default EventosTab;
